import { Show, For, createSignal, type Component } from "solid-js";
import { type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { Drawer } from "@/shared/drawer";
import { Skeleton } from "@/components/skeleton";
import "./versions.css";

// ─── Types VSS (Win + macOS TM) ───────────────────────────────────────────

interface Snapshot {
  id: string;
  date: string | null;
  volume: string | null;
  mountedPath: string;
  originatingMachine?: string;
  clientAccessible?: boolean;
}

interface FileVersionEntry {
  shadowId: string;
  snapshotAt: string | null;
  sizeBytes: number;
  lastWrite: string | null;
  shadowPath: string;
}

interface FileVersionsResult {
  filePath: string;
  currentExists: boolean;
  currentSize: number;
  currentLastWrite: string | null;
  versions: FileVersionEntry[];
}

interface SearchResultRow {
  shadowId: string;
  snapshotAt: string | null;
  name: string;
  fullPath: string;
  sizeBytes: number;
  lastWrite: string | null;
}

interface SearchResult {
  pattern: string;
  scopeDir: string | null;
  count: number;
  truncated: boolean;
  rows: SearchResultRow[];
}

interface ListResult<T> {
  count?: number;
  rows?: T[];
}

interface Capabilities {
  os: "win32" | "darwin";
  backend: "vss" | "timemachine";
}

// ─── Types file_* (builtin C#) ───────────────────────────────────────────

interface BrowseEntry {
  name: string;
  type: "directory" | "file";
  size: number | null;
  modified: string | null;
}

interface BrowseResult {
  path: string;
  entries: BrowseEntry[];
}

interface FileReadResult {
  path: string;
  content: string;
  size: number;
}

// ─── Types file_info ──────────────────────────────────────────────────────

interface AclEntry {
  identity: string;
  type: "Allow" | "Deny";
  rights: string;
  inherited: boolean;
  propagation: string;
  inheritance: string;
}

interface FileMetadata {
  fileVersion?: string | null;
  productVersion?: string | null;
  productName?: string | null;
  companyName?: string | null;
  fileDescription?: string | null;
  originalFilename?: string | null;
  internalName?: string | null;
  legalCopyright?: string | null;
  language?: string | null;
  isDebug?: boolean;
  isPatched?: boolean;
  isPreRelease?: boolean;
}

interface FileSignature {
  signed: boolean;
  subject?: string;
  issuer?: string;
  thumbprint?: string;
  serialNumber?: string;
  notBefore?: string;
  notAfter?: string;
  hasPrivateKey?: boolean;
  signatureAlgorithm?: string;
}

interface FileInfoResult {
  path: string;
  isDirectory: boolean;
  isFile: boolean;
  sizeBytes: number | null;
  createdAt: string;
  modifiedAt: string;
  accessedAt: string;
  attributes: string;
  isReadOnly: boolean;
  isHidden: boolean;
  isSystem: boolean;
  isArchive: boolean;
  isCompressed: boolean;
  isEncrypted: boolean;
  isReparsePoint: boolean;
  owner: string | null;
  ownerSid: string | null;
  acl: AclEntry[];
  metadata?: FileMetadata | null;
  signature?: FileSignature | null;
}

// ─── Types file_search ───────────────────────────────────────────────────

interface FileSearchEntry {
  name: string;
  path: string;
  type: "file" | "directory";
  size: number | null;
  modified: string | null;
}

interface FileSearchResult {
  root: string;
  pattern: string;
  count: number;
  truncated: boolean;
  matches: FileSearchEntry[];
  warning?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

const UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

/** Taille max d'un fichier prévisualisable en aperçu rapide (1 Mo). */
const QUICKLOOK_MAX_BYTES = 1024 * 1024;

const IMAGE_EXTS = new Set(["png", "jpg", "jpeg", "gif", "bmp", "webp", "ico", "svg"]);
const TEXT_EXTS = new Set([
  "txt",
  "log",
  "md",
  "json",
  "xml",
  "yaml",
  "yml",
  "ini",
  "cfg",
  "conf",
  "csv",
  "tsv",
  "html",
  "htm",
  "css",
  "js",
  "ts",
  "jsx",
  "tsx",
  "sh",
  "ps1",
  "bat",
  "cmd",
  "py",
  "rb",
  "go",
  "rs",
  "java",
  "c",
  "cpp",
  "h",
  "hpp",
  "cs",
  "sql",
  "toml",
  "env",
  "gitignore",
  "properties",
  "manifest",
  "reg",
]);

/** Renvoie l'extension (sans point, en minuscules) d'un nom de fichier. */
function fileExt(name: string): string {
  const idx = name.lastIndexOf(".");
  if (idx <= 0 || idx === name.length - 1) return "";
  return name.slice(idx + 1).toLowerCase();
}

/** Type MIME approximatif pour une extension image. */
function imageMime(ext: string): string {
  switch (ext) {
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "svg":
      return "image/svg+xml";
    case "ico":
      return "image/x-icon";
    default:
      return `image/${ext}`;
  }
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}

function fmtBytes(b: number | null | undefined): string {
  if (b == null || b === 0) return "0";
  const units = ["B", "kB", "MB", "GB"];
  let n = b;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/** Détecte le séparateur d'un chemin (Windows vs Unix). */
function pathSep(p: string): "\\" | "/" {
  if (!p) return "\\";
  if (p.includes("\\")) return "\\";
  if (p.startsWith("/")) return "/";
  return "\\";
}

/** Joint un dossier et un nom avec le bon séparateur. */
function pathJoin(dir: string, name: string): string {
  if (!dir) return name;
  const sep = pathSep(dir);
  const trimmed = dir.endsWith(sep) ? dir.slice(0, -1) : dir;
  return `${trimmed}${sep}${name}`;
}

/**
 * Construit un chemin de snapshot VSS à partir d'un mountedPath et d'un chemin relatif "live".
 * - mountedPath : ex. "\\?\GLOBALROOT\Device\HarddiskVolumeShadowCopy42\"
 * - relativePath : ex. "C:\Users\Alice" → la lettre de volume est retirée, le reste est concaténé.
 *   Si relativePath est "C:" ou "C:\" → renvoie mountedPath tel quel.
 */
function makeSnapshotPath(mountedPath: string, relativePath: string): string {
  if (!mountedPath) return relativePath;
  const mp = mountedPath.endsWith("\\") ? mountedPath.slice(0, -1) : mountedPath;
  const rel = (relativePath || "").trim();
  if (!rel) return mp + "\\";
  // Retire la lettre de volume (ex: "C:") si présente.
  let sub = rel;
  if (/^[A-Za-z]:/.test(sub)) sub = sub.slice(2);
  // Normalise le slash de tête.
  if (sub.startsWith("\\") || sub.startsWith("/")) sub = sub.slice(1);
  if (!sub) return mp + "\\";
  return pathJoin(mp, sub);
}

/** Renvoie le dossier parent ou null si on est à la racine. */
function pathParent(p: string): string | null {
  if (!p) return null;
  const sep = pathSep(p);
  // Racine Windows : "C:\" ou "C:"
  if (/^[A-Za-z]:[\\/]?$/.test(p)) return null;
  // Racine Unix
  if (p === "/" || p === sep) return null;
  const trimmed = p.endsWith(sep) ? p.slice(0, -1) : p;
  const idx = trimmed.lastIndexOf(sep);
  if (idx < 0) return null;
  if (idx === 0) return sep; // Unix root
  // Sur Windows, "C:\foo" → parent = "C:\"
  if (/^[A-Za-z]:$/.test(trimmed.slice(0, idx))) return trimmed.slice(0, idx) + sep;
  return trimmed.slice(0, idx);
}

/** Construit les segments cliquables d'un breadcrumb. */
function breadcrumbSegments(p: string): { label: string; full: string }[] {
  if (!p) return [];
  const sep = pathSep(p);
  const out: { label: string; full: string }[] = [];

  if (sep === "\\") {
    // Windows : "C:\Users\Alice" → ["C:\", "Users", "Alice"]
    const m = /^([A-Za-z]:)([\\/].*)?$/.exec(p);
    if (!m) return [{ label: p, full: p }];
    const drive = m[1];
    const rest = (m[2] || "").replace(/^[\\/]/, "");
    out.push({ label: drive + "\\", full: drive + "\\" });
    if (rest) {
      const parts = rest.split(/[\\/]/).filter(Boolean);
      let cur = drive + "\\";
      for (const part of parts) {
        cur = cur.endsWith("\\") ? cur + part : cur + "\\" + part;
        out.push({ label: part, full: cur });
      }
    }
  } else {
    // Unix : "/Users/alice" → ["/", "Users", "alice"]
    out.push({ label: "/", full: "/" });
    const parts = p.split("/").filter(Boolean);
    let cur = "";
    for (const part of parts) {
      cur += "/" + part;
      out.push({ label: part, full: cur });
    }
  }
  return out;
}

/** Décode une chaîne base64 en Uint8Array. */
function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/\s+/g, "");
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

/** Déclenche un téléchargement navigateur d'un blob. */
function triggerDownload(bytes: Uint8Array, filename: string): void {
  // Copie dans un ArrayBuffer "non partagé" pour satisfaire les types BlobPart stricts.
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  const blob = new Blob([buf], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Lit un File en base64 (sans le préfixe data:). */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const idx = dataUrl.indexOf(",");
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error || new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}

// ─── Composant principal ──────────────────────────────────────────────────

const VersionsPanel: Component = () => {
  const ws = useWs();
  const tools = useToolClient();
  const confirm = useConfirm();

  const [status, setStatus] = createSignal<PanelStatus | null>(null);

  // ─── État Versions (VSS) ────────────────────────────────────────────────
  const [filePath, setFilePath] = createSignal("");
  const [fileResult, setFileResult] = createSignal<FileVersionsResult | null>(null);
  const [fileLoading, setFileLoading] = createSignal(false);

  const [searchPattern, setSearchPattern] = createSignal("");
  const [searchScope, setSearchScope] = createSignal("");
  const [searchResult, setSearchResult] = createSignal<SearchResult | null>(null);
  const [searchLoading, setSearchLoading] = createSignal(false);

  // ─── État Parcourir ─────────────────────────────────────────────────────
  const [browsePath, setBrowsePath] = createSignal("C:\\");
  const [browsePathInput, setBrowsePathInput] = createSignal("C:\\");
  const [browseResult, setBrowseResult] = createSignal<BrowseResult | null>(null);
  const [browseLoading, setBrowseLoading] = createSignal(false);
  const [browseError, setBrowseError] = createSignal<string | null>(null);
  // Snapshot actif pour la navigation (vue read-only via VSS). "" = live.
  const [snapshotMount, setSnapshotMount] = createSignal("");
  const [snapshotDate, setSnapshotDate] = createSignal<string | null>(null);

  // ─── État Modale Info ───────────────────────────────────────────────────
  const [infoOpen, setInfoOpen] = createSignal(false);
  const [infoLoading, setInfoLoading] = createSignal(false);
  const [infoError, setInfoError] = createSignal<string | null>(null);
  const [infoData, setInfoData] = createSignal<FileInfoResult | null>(null);
  const [infoTargetPath, setInfoTargetPath] = createSignal("");

  // ─── État Recherche par nom (file_search récursive) ────────────────────
  const [nameSearchPattern, setNameSearchPattern] = createSignal("");
  const [nameSearchLoading, setNameSearchLoading] = createSignal(false);
  const [nameSearchResult, setNameSearchResult] = createSignal<FileSearchResult | null>(null);

  // ─── État Téléverser ────────────────────────────────────────────────────
  const [uploadFile, setUploadFile] = createSignal<File | null>(null);
  const [uploadTargetDir, setUploadTargetDir] = createSignal("C:\\");
  const [uploading, setUploading] = createSignal(false);
  // Compteur de profondeur dragenter/dragleave (évite le flicker sur les enfants).
  const [dragDepth, setDragDepth] = createSignal(0);

  // ─── État Aperçu rapide (Quick Look) ────────────────────────────────────
  const [quickOpen, setQuickOpen] = createSignal(false);
  const [quickLoading, setQuickLoading] = createSignal(false);
  const [quickError, setQuickError] = createSignal<string | null>(null);
  const [quickName, setQuickName] = createSignal("");
  const [quickKind, setQuickKind] = createSignal<"text" | "image" | "none">("none");
  const [quickText, setQuickText] = createSignal("");
  const [quickImageUrl, setQuickImageUrl] = createSignal("");
  const [quickPath, setQuickPath] = createSignal("");
  const [quickSize, setQuickSize] = createSignal<number | null>(null);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  // ─── Queries VSS ────────────────────────────────────────────────────────

  const capsQuery = createToolQuery<Capabilities>(() => ({
    tool: "versions_capabilities",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const snapshotsQuery = createToolQuery<ListResult<Snapshot>>(() => ({
    tool: "versions_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  async function loadFileVersions(): Promise<void> {
    const p = filePath().trim();
    if (!p) {
      setStatus({ text: "Saisir un chemin de fichier.", level: "warn" });
      return;
    }
    setFileLoading(true);
    setStatus({ text: `Recherche des versions de ${p}…`, level: "info" });
    try {
      const r = await tools.call<FileVersionsResult>("versions_file_versions", { filePath: p });
      setFileResult(r);
      setStatus({
        text: `${r.versions.length} version${r.versions.length > 1 ? "s" : ""} trouvée${r.versions.length > 1 ? "s" : ""}.`,
        level: r.versions.length ? "info" : "warn",
      });
    } catch (err) {
      setStatus({ text: `Erreur: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
      setFileResult(null);
    } finally {
      setFileLoading(false);
    }
  }

  async function runSearch(): Promise<void> {
    const pattern = searchPattern().trim();
    if (!pattern) {
      setStatus({ text: "Saisir un motif de recherche.", level: "warn" });
      return;
    }
    setSearchLoading(true);
    setStatus({ text: `Recherche "${pattern}" dans les snapshots…`, level: "info" });
    try {
      const r = await tools.call<SearchResult>("versions_search", {
        pattern,
        scopeDir: searchScope().trim() || undefined,
        maxResults: 200,
      });
      setSearchResult(r);
      setStatus({
        text: `${r.count} résultat${r.count > 1 ? "s" : ""}${r.truncated ? " (tronqué)" : ""}.`,
        level: r.count ? "info" : "warn",
      });
    } catch (err) {
      setStatus({ text: `Erreur recherche: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
      setSearchResult(null);
    } finally {
      setSearchLoading(false);
    }
  }

  async function restore(shadowPath: string, suggestedDest: string): Promise<void> {
    const dest = window.prompt("Restaurer vers (chemin destination) :", suggestedDest);
    if (!dest) return;
    const ok = await confirm.confirm({
      title: "Restaurer le fichier",
      body: `Copier depuis le snapshot vers :\n${dest}\n\nSi le fichier existe déjà à cette destination, il sera écrasé.`,
      level: "warn",
      confirmLabel: "Restaurer",
    });
    if (!ok) return;
    setStatus({ text: `Restauration en cours vers ${dest}…`, level: "info" });
    try {
      const r = await tools.call<{ destPath: string; sizeBytes?: number }>(
        "versions_restore",
        { shadowPath, destPath: dest },
        { userConfirmedAt: new Date().toISOString() },
      );
      setStatus({
        text: `Restauré : ${r.destPath}${r.sizeBytes ? ` (${fmtBytes(r.sizeBytes)})` : ""}.`,
        level: "info",
      });
      if (fileResult()) await loadFileVersions();
    } catch (err) {
      setStatus({ text: `Échec restauration: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
    }
  }

  // ─── Parcourir : actions ───────────────────────────────────────────────

  async function loadBrowse(p: string): Promise<void> {
    const target = p.trim();
    if (!target) {
      setBrowseError("Saisir un chemin.");
      return;
    }
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      // En mode snapshot, on envoie au backend un chemin réécrit (GLOBALROOT...)
      // mais on garde le chemin "live" pour l'affichage.
      const mount = snapshotMount();
      const sentPath = mount ? makeSnapshotPath(mount, target) : target;
      const r = await tools.call<BrowseResult>("file_list", { path: sentPath });
      setBrowseResult({ path: target, entries: r.entries });
      setBrowsePath(target);
      setBrowsePathInput(target);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setBrowseError(msg);
      setBrowseResult(null);
    } finally {
      setBrowseLoading(false);
    }
  }

  /** Sortir du mode snapshot et revenir à la navigation live. */
  function exitSnapshotMode(): void {
    if (!snapshotMount()) return;
    setSnapshotMount("");
    setSnapshotDate(null);
    void loadBrowse(browsePath());
  }

  /** Entre dans une snapshot et recharge le dossier courant (en relatif). */
  function enterSnapshot(mount: string, label: string | null): void {
    setSnapshotMount(mount);
    setSnapshotDate(label);
    void loadBrowse(browsePath());
  }

  function goUp(): void {
    const parent = pathParent(browsePath());
    if (parent) void loadBrowse(parent);
  }

  async function downloadEntry(entry: BrowseEntry): Promise<void> {
    if (entry.type !== "file") return;
    const full = pathJoin(browsePath(), entry.name);
    const mount = snapshotMount();
    const fullForRead = mount ? makeSnapshotPath(mount, full) : full;
    if (entry.size != null && entry.size > UPLOAD_MAX_BYTES) {
      const ok = await confirm.confirm({
        title: "Fichier volumineux",
        body: `${entry.name} fait ${fmtBytes(entry.size)}. Le téléchargement peut être lent. Continuer ?`,
        level: "warn",
        confirmLabel: "Télécharger",
      });
      if (!ok) return;
    }
    setStatus({ text: `Téléchargement de ${entry.name}…`, level: "info" });
    try {
      const r = await tools.call<FileReadResult>("file_read", { path: fullForRead, encoding: "base64" });
      let bytes: Uint8Array;
      try {
        bytes = base64ToBytes(r.content || "");
      } catch {
        // Fallback : si le serveur a renvoyé du texte brut (encoding non supporté),
        // on tente une conversion latin1 byte-par-byte.
        const s = r.content || "";
        bytes = new Uint8Array(s.length);
        for (let i = 0; i < s.length; i += 1) bytes[i] = s.charCodeAt(i) & 0xff;
      }
      triggerDownload(bytes, entry.name);
      setStatus({ text: `${entry.name} téléchargé (${fmtBytes(bytes.byteLength)}).`, level: "info" });
    } catch (err) {
      setStatus({
        text: `Échec téléchargement: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function deleteEntry(entry: BrowseEntry): Promise<void> {
    const full = pathJoin(browsePath(), entry.name);
    const ok = await confirm.confirm({
      title: "Supprimer",
      body: `Supprimer ${entry.type === "directory" ? "le dossier" : "le fichier"} :\n${full}\n\nCette action est irréversible.`,
      level: "danger",
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    setStatus({ text: `Suppression de ${entry.name}…`, level: "info" });
    try {
      await tools.call("file_delete", { path: full }, { userConfirmedAt: new Date().toISOString() });
      setStatus({ text: `${entry.name} supprimé.`, level: "info" });
      await loadBrowse(browsePath());
    } catch (err) {
      setStatus({ text: `Échec suppression: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
    }
  }

  function openEntry(entry: BrowseEntry): void {
    if (entry.type !== "directory") return;
    void loadBrowse(pathJoin(browsePath(), entry.name));
  }

  function showVersionsFor(entry: BrowseEntry): void {
    if (entry.type !== "file") return;
    const full = pathJoin(browsePath(), entry.name);
    setFilePath(full);
    setActiveSub("history");
    setStatus({ text: "Onglet « Versions précédentes » : cliquez sur « Chercher les versions ».", level: "info" });
    void loadFileVersions();
  }

  /**
   * Active la vue snapshot pour un chemin donné (utilisé depuis le dropdown par-ligne).
   * Pour un dossier : on entre dans le snapshot et on ouvre directement le sous-dossier.
   * Pour un fichier : on entre dans le snapshot et on reste dans le dossier courant.
   */
  function enterSnapshotForEntry(entry: BrowseEntry, mount: string, label: string | null): void {
    setSnapshotMount(mount);
    setSnapshotDate(label);
    if (entry.type === "directory") {
      void loadBrowse(pathJoin(browsePath(), entry.name));
    } else {
      void loadBrowse(browsePath());
    }
  }

  // ─── Recherche par nom (file_search récursive) ─────────────────────────

  async function runNameSearch(): Promise<void> {
    const pattern = nameSearchPattern().trim();
    if (!pattern) {
      setStatus({ text: "Saisir un motif (ex: *.log, budget*).", level: "warn" });
      return;
    }
    const rootRaw = browsePath().trim();
    if (!rootRaw) {
      setStatus({ text: "Aucun dossier courant — saisir un chemin de départ.", level: "warn" });
      return;
    }
    const mount = snapshotMount();
    const root = mount ? makeSnapshotPath(mount, rootRaw) : rootRaw;
    // En mode snapshot, le pattern peut inclure des wildcards ; sinon on enrobe
    // automatiquement si l'utilisateur a tapé un fragment sans wildcard.
    const finalPattern = /[*?]/.test(pattern) ? pattern : `*${pattern}*`;
    setNameSearchLoading(true);
    setStatus({ text: `Recherche "${finalPattern}" sous ${rootRaw}…`, level: "info" });
    try {
      const r = await tools.call<FileSearchResult>("file_search", {
        path: root,
        pattern: finalPattern,
        maxResults: 500,
        includeHidden: true,
        filesOnly: false,
      });
      setNameSearchResult(r);
      setStatus({
        text: `${r.count} résultat${r.count > 1 ? "s" : ""}${r.truncated ? " (tronqué)" : ""}.`,
        level: r.count ? "info" : "warn",
      });
    } catch (err) {
      setStatus({ text: `Erreur recherche: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
      setNameSearchResult(null);
    } finally {
      setNameSearchLoading(false);
    }
  }

  /** Navigue vers un résultat de recherche : si dossier → openBrowse, si fichier → openBrowse du parent. */
  function navigateToSearchHit(row: FileSearchEntry): void {
    if (row.type === "directory") {
      void loadBrowse(row.path);
    } else {
      const parent = pathParent(row.path);
      if (parent) void loadBrowse(parent);
    }
  }

  // ─── Modale Info : load ────────────────────────────────────────────────

  async function openInfoModal(targetPath: string): Promise<void> {
    setInfoTargetPath(targetPath);
    setInfoOpen(true);
    setInfoData(null);
    setInfoError(null);
    setInfoLoading(true);
    try {
      const r = await tools.call<FileInfoResult>("file_info", { path: targetPath });
      setInfoData(r);
    } catch (err) {
      setInfoError(err instanceof Error ? err.message : String(err));
    } finally {
      setInfoLoading(false);
    }
  }

  function openInfoForEntry(entry: BrowseEntry): void {
    const full = pathJoin(browsePath(), entry.name);
    const mount = snapshotMount();
    void openInfoModal(mount ? makeSnapshotPath(mount, full) : full);
  }

  function closeInfo(): void {
    setInfoOpen(false);
    setInfoData(null);
    setInfoError(null);
    setInfoTargetPath("");
  }

  // ─── Téléverser : action ───────────────────────────────────────────────

  async function doUpload(): Promise<void> {
    const file = uploadFile();
    const dir = uploadTargetDir().trim();
    if (!file) {
      setStatus({ text: "Sélectionner un fichier local.", level: "warn" });
      return;
    }
    if (!dir) {
      setStatus({ text: "Saisir le dossier cible.", level: "warn" });
      return;
    }
    if (file.size > UPLOAD_MAX_BYTES) {
      setStatus({
        text: `Fichier trop volumineux (${fmtBytes(file.size)} > ${fmtBytes(UPLOAD_MAX_BYTES)}).`,
        level: "danger",
      });
      return;
    }
    const targetPath = pathJoin(dir, file.name);
    setUploading(true);
    setStatus({ text: `Téléversement de ${file.name} vers ${targetPath}…`, level: "info" });
    try {
      const base64 = await readFileAsBase64(file);
      await tools.call(
        "file_write",
        { path: targetPath, content: base64, encoding: "base64" },
        { userConfirmedAt: new Date().toISOString() },
      );
      setStatus({ text: `${file.name} téléversé vers ${targetPath} (${fmtBytes(file.size)}).`, level: "info" });
      setUploadFile(null);
      // Si le dossier cible correspond au dossier affiché → rafraîchir.
      if (
        browsePath()
          .toLowerCase()
          .replace(/[\\/]+$/, "") === dir.toLowerCase().replace(/[\\/]+$/, "")
      ) {
        await loadBrowse(browsePath());
      }
    } catch (err) {
      setStatus({ text: `Échec téléversement: ${err instanceof Error ? err.message : String(err)}`, level: "danger" });
    } finally {
      setUploading(false);
    }
  }

  /**
   * Téléverse une liste de fichiers (issus d'un drag & drop) vers un dossier cible.
   * Réutilise le même outil `file_write` (encodage base64) que le formulaire classique.
   * Le dossier cible courant est celui actuellement parcouru ; en mode snapshot
   * (lecture seule) l'upload est refusé proprement.
   */
  async function uploadDroppedFiles(files: File[], dir: string): Promise<void> {
    if (snapshotMount()) {
      setStatus({ text: "Upload non disponible : vue snapshot en lecture seule.", level: "warn" });
      return;
    }
    const targetDir = dir.trim();
    if (!targetDir) {
      setStatus({ text: "Aucun dossier cible — ouvrez d'abord un dossier.", level: "warn" });
      return;
    }
    const accepted = files.filter((f) => f.size <= UPLOAD_MAX_BYTES);
    const rejected = files.filter((f) => f.size > UPLOAD_MAX_BYTES);
    if (!accepted.length) {
      setStatus({
        text: `Aucun fichier accepté — limite ${fmtBytes(UPLOAD_MAX_BYTES)} par fichier.`,
        level: "danger",
      });
      return;
    }
    setUploading(true);
    let done = 0;
    try {
      for (const file of accepted) {
        const targetPath = pathJoin(targetDir, file.name);
        setStatus({
          text: `Téléversement ${done + 1}/${accepted.length} : ${file.name}…`,
          level: "info",
        });
        const base64 = await readFileAsBase64(file);
        await tools.call(
          "file_write",
          { path: targetPath, content: base64, encoding: "base64" },
          { userConfirmedAt: new Date().toISOString() },
        );
        done += 1;
      }
      const skipped = rejected.length ? ` — ${rejected.length} ignoré(s) (trop volumineux)` : "";
      setStatus({ text: `${done} fichier(s) téléversé(s) vers ${targetDir}${skipped}.`, level: "info" });
      await loadBrowse(browsePath());
    } catch (err) {
      setStatus({
        text: `Échec téléversement (${done}/${accepted.length} ok): ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    } finally {
      setUploading(false);
    }
  }

  // ─── Aperçu rapide (Quick Look) ────────────────────────────────────────

  function closeQuickLook(): void {
    setQuickOpen(false);
    const url = quickImageUrl();
    if (url) URL.revokeObjectURL(url);
    setQuickImageUrl("");
    setQuickText("");
    setQuickKind("none");
    setQuickError(null);
    setQuickName("");
    setQuickPath("");
    setQuickSize(null);
  }

  /**
   * Ouvre l'aperçu rapide d'un fichier de la vue Parcourir.
   * - Image (extension connue) : décodée en blob → affichée.
   * - Texte (extension connue) de taille ≤ QUICKLOOK_MAX_BYTES : contenu affiché.
   * - Autre / trop gros : message clair invitant au téléchargement complet.
   */
  async function openQuickLook(entry: BrowseEntry): Promise<void> {
    if (entry.type !== "file") return;
    const full = pathJoin(browsePath(), entry.name);
    const mount = snapshotMount();
    const readPath = mount ? makeSnapshotPath(mount, full) : full;
    const ext = fileExt(entry.name);
    const isImage = IMAGE_EXTS.has(ext);
    const isText = TEXT_EXTS.has(ext) || ext === "";

    // Révoque une éventuelle image précédente.
    const prevUrl = quickImageUrl();
    if (prevUrl) URL.revokeObjectURL(prevUrl);
    setQuickImageUrl("");
    setQuickText("");
    setQuickError(null);
    setQuickName(entry.name);
    setQuickPath(full);
    setQuickSize(entry.size ?? null);
    setQuickOpen(true);

    if (!isImage && !isText) {
      setQuickKind("none");
      setQuickError("Type de fichier non prévisualisable. Utilisez le téléchargement complet.");
      return;
    }
    if (entry.size != null && entry.size > QUICKLOOK_MAX_BYTES) {
      setQuickKind("none");
      setQuickError(
        `Fichier trop volumineux pour l'aperçu (${fmtBytes(entry.size)} > ${fmtBytes(QUICKLOOK_MAX_BYTES)}). Utilisez le téléchargement complet.`,
      );
      return;
    }

    setQuickKind(isImage ? "image" : "text");
    setQuickLoading(true);
    try {
      if (isImage) {
        const r = await tools.call<FileReadResult>("file_read", { path: readPath, encoding: "base64" });
        const bytes = base64ToBytes(r.content || "");
        const buf = new ArrayBuffer(bytes.byteLength);
        new Uint8Array(buf).set(bytes);
        const blob = new Blob([buf], { type: imageMime(ext) });
        setQuickImageUrl(URL.createObjectURL(blob));
      } else {
        const r = await tools.call<FileReadResult>("file_read", { path: readPath, encoding: "utf8" });
        setQuickText(r.content || "");
      }
    } catch (err) {
      setQuickError(err instanceof Error ? err.message : String(err));
    } finally {
      setQuickLoading(false);
    }
  }

  // ─── Onglet Parcourir ──────────────────────────────────────────────────

  const browseColumns: Column<BrowseEntry>[] = [
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: (info) => ((info.getValue() as string) === "directory" ? "📁" : "📄"),
    },
    {
      id: "name",
      header: "Nom",
      accessorKey: "name",
      cell: (info) => {
        const row = info.row.original as BrowseEntry;
        const name = String(info.getValue() || "");
        return (
          <Show when={row.type === "directory"} fallback={<span>{name}</span>}>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              style={{ padding: "0", "text-align": "left", color: "var(--link, #4ea3ff)" }}
              onClick={() => openEntry(row)}
            >
              {name}
            </button>
          </Show>
        );
      },
    },
    {
      id: "size",
      header: "Taille",
      accessorKey: "size",
      cell: (info) => {
        const row = info.row.original as BrowseEntry;
        return row.type === "directory" ? "—" : fmtBytes(info.getValue() as number);
      },
    },
    {
      id: "modified",
      header: "Modifié",
      accessorKey: "modified",
      cell: (info) => fmtDate(info.getValue() as string | null),
    },
  ];

  const dragActive = () => dragDepth() > 0;

  const BrowseTab = () => (
    <div
      class="panel-data versions-dropzone"
      classList={{
        "versions-dropzone--active": dragActive() && !snapshotMount(),
        "versions-dropzone--blocked": dragActive() && !!snapshotMount(),
      }}
      style={{ display: "flex", "flex-direction": "column", gap: "12px", position: "relative" }}
      onDragEnter={(e) => {
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        setDragDepth((d) => d + 1);
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = snapshotMount() ? "none" : "copy";
      }}
      onDragLeave={(e) => {
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types).includes("Files")) return;
        setDragDepth((d) => Math.max(0, d - 1));
      }}
      onDrop={(e) => {
        if (!e.dataTransfer || !Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        setDragDepth(0);
        const files = Array.from(e.dataTransfer.files);
        if (files.length) void uploadDroppedFiles(files, browsePath());
      }}
    >
      {/* Overlay drag & drop */}
      <Show when={dragActive()}>
        <div class="versions-drop-overlay">
          <div class="versions-drop-overlay__inner">
            <Show
              when={!snapshotMount()}
              fallback={
                <>
                  <div class="versions-drop-overlay__icon">⛔</div>
                  <strong>Upload non disponible</strong>
                  <span>La vue snapshot est en lecture seule.</span>
                </>
              }
            >
              <div class="versions-drop-overlay__icon">📥</div>
              <strong>Déposer pour téléverser</strong>
              <span>
                Vers <code>{browsePath() || "—"}</code>
              </span>
            </Show>
          </div>
        </div>
      </Show>

      {/* Sélecteur de snapshot (navigation read-only via VSS) */}
      <div class="filter-row">
        <label style={{ display: "flex", "align-items": "center", gap: "6px", "font-size": "12px" }}>
          <span style={{ opacity: 0.8 }}>Snapshot :</span>
          <select
            class="input"
            value={snapshotMount()}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (!v) {
                exitSnapshotMode();
              } else {
                const snap = (snapshotsQuery.data?.rows ?? []).find((s) => s.mountedPath === v);
                enterSnapshot(v, snap ? `${fmtDate(snap.date)} — ${snap.volume || ""}` : null);
              }
            }}
            style={{ "min-width": "280px" }}
          >
            <option value="">Système actif</option>
            <For each={snapshotsQuery.data?.rows ?? []}>
              {(s) => (
                <option value={s.mountedPath}>
                  {fmtDate(s.date)} — {s.volume || "?"}
                </option>
              )}
            </For>
          </select>
        </label>
      </div>

      <div class="filter-row">
        <button type="button" class="btn btn-ghost btn-sm" onClick={goUp} title="Remonter d'un dossier">
          ⬆
        </button>
        <input
          class="input"
          type="text"
          placeholder="Chemin (ex: C:\\Users\\Alice)"
          value={browsePathInput()}
          onInput={(e) => setBrowsePathInput(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void loadBrowse(browsePathInput());
          }}
          style={{ flex: "1", "min-width": "280px" }}
        />
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => void loadBrowse(browsePathInput())}
          disabled={browseLoading()}
        >
          {browseLoading() ? "Chargement…" : "Ouvrir"}
        </button>
      </div>

      {/* Bannière mode snapshot */}
      <Show when={snapshotMount()}>
        <div
          style={{
            display: "flex",
            "align-items": "center",
            "justify-content": "space-between",
            gap: "12px",
            padding: "8px 12px",
            background: "rgba(245, 166, 35, 0.15)",
            border: "1px solid rgba(245, 166, 35, 0.5)",
            "border-radius": "6px",
            color: "#f5a623",
            "font-size": "13px",
          }}
        >
          <span>
            🕒 Vue snapshot du <strong>{snapshotDate() || "—"}</strong> — lecture seule
          </span>
          <button type="button" class="btn btn-ghost btn-sm" onClick={exitSnapshotMode}>
            ← Retour au live
          </button>
        </div>
      </Show>

      <Show when={breadcrumbSegments(browsePath()).length > 0}>
        <div style={{ display: "flex", "flex-wrap": "wrap", gap: "4px", "font-size": "12px" }}>
          <For each={breadcrumbSegments(browsePath())}>
            {(seg, idx) => (
              <>
                <Show when={idx() > 0}>
                  <span style={{ opacity: 0.5 }}>{pathSep(browsePath())}</span>
                </Show>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  style={{ padding: "0 4px", color: "var(--link, #4ea3ff)" }}
                  onClick={() => void loadBrowse(seg.full)}
                >
                  {seg.label}
                </button>
              </>
            )}
          </For>
        </div>
      </Show>

      {/* Recherche récursive par nom dans le dossier courant */}
      <div class="filter-row">
        <input
          class="input"
          type="text"
          placeholder="🔎 Recherche récursive de nom (ex: *.log, budget, photo*.jpg)"
          value={nameSearchPattern()}
          onInput={(e) => setNameSearchPattern(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void runNameSearch();
          }}
          style={{ flex: "1", "min-width": "240px" }}
        />
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => void runNameSearch()}
          disabled={nameSearchLoading()}
          title={`Recherche depuis : ${browsePath()}`}
        >
          {nameSearchLoading() ? "Recherche…" : "Rechercher"}
        </button>
        <Show when={nameSearchResult()}>
          <button
            type="button"
            class="btn btn-ghost btn-sm"
            onClick={() => {
              setNameSearchResult(null);
              setNameSearchPattern("");
            }}
            title="Effacer les résultats"
          >
            ✕
          </button>
        </Show>
      </div>

      {/* Résultats de recherche par nom */}
      <Show when={nameSearchResult()}>
        {(r) => (
          <div
            style={{
              border: "1px solid #3a3a3a",
              "border-radius": "6px",
              padding: "8px",
              background: "rgba(78, 163, 255, 0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                "align-items": "center",
                "justify-content": "space-between",
                "margin-bottom": "8px",
              }}
            >
              <strong style={{ "font-size": "12px" }}>
                {r().count} résultat{r().count > 1 ? "s" : ""} pour <code>{r().pattern}</code>
                <Show when={r().truncated}>
                  <span style={{ color: "var(--warn, #f5a623)", "margin-left": "8px" }}>(tronqué à 500)</span>
                </Show>
              </strong>
            </div>
            <DataTable
              rows={r().matches}
              columns={[
                {
                  id: "type",
                  header: "",
                  accessorKey: "type",
                  cell: (info) => ((info.getValue() as string) === "directory" ? "📁" : "📄"),
                },
                { id: "name", header: "Nom", accessorKey: "name" },
                {
                  id: "path",
                  header: "Chemin",
                  accessorKey: "path",
                  cell: (info) => {
                    const v = String(info.getValue() || "");
                    return v.length > 100 ? "…" + v.slice(-100) : v;
                  },
                },
                {
                  id: "size",
                  header: "Taille",
                  accessorKey: "size",
                  cell: (info) => {
                    const row = info.row.original as FileSearchEntry;
                    return row.type === "directory" ? "—" : fmtBytes(info.getValue() as number);
                  },
                },
                {
                  id: "modified",
                  header: "Modifié",
                  accessorKey: "modified",
                  cell: (info) => fmtDate(info.getValue() as string | null),
                },
              ]}
              emptyMessage="Aucun résultat."
              rowActions={(row) => (
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    title="Informations"
                    onClick={() => void openInfoModal(row.path)}
                  >
                    ℹ
                  </button>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    title={row.type === "directory" ? "Ouvrir le dossier" : "Aller au dossier parent"}
                    onClick={() => navigateToSearchHit(row)}
                  >
                    ↗
                  </button>
                </div>
              )}
            />
          </div>
        )}
      </Show>

      <Show when={browseError()}>{(err) => <div class="empty-state empty-state--error">Erreur : {err()}</div>}</Show>

      <Show when={browseLoading()}>
        <div class="versions-skeleton">
          <Skeleton variant="table" rows={8} />
        </div>
      </Show>

      <Show when={!browseError() && !browseLoading() ? browseResult() : null}>
        {(r) => (
          <DataTable
            rows={r().entries}
            columns={browseColumns}
            emptyMessage="Dossier vide."
            rowActions={(row) => (
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  title="Informations détaillées"
                  onClick={() => openInfoForEntry(row)}
                >
                  ℹ
                </button>
                <Show when={row.type === "file"}>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    title="Aperçu rapide"
                    onClick={() => void openQuickLook(row)}
                  >
                    👁
                  </button>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    title="Télécharger"
                    onClick={() => void downloadEntry(row)}
                  >
                    📥
                  </button>
                </Show>
                {/* Dropdown versions : disponible pour fichier ET dossier en mode live */}
                <Show when={!snapshotMount() && (snapshotsQuery.data?.rows ?? []).length > 0}>
                  <select
                    class="input"
                    title={
                      row.type === "directory"
                        ? "Naviguer dans une version précédente du dossier"
                        : "Naviguer dans une version précédente du fichier"
                    }
                    style={{ padding: "2px 6px", "font-size": "11px", "max-width": "140px" }}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      e.currentTarget.value = "";
                      if (!v) return;
                      if (v === "__file_versions__") {
                        showVersionsFor(row);
                        return;
                      }
                      const snap = (snapshotsQuery.data?.rows ?? []).find((s) => s.mountedPath === v);
                      if (snap) {
                        enterSnapshotForEntry(row, snap.mountedPath, `${fmtDate(snap.date)} — ${snap.volume || ""}`);
                      }
                    }}
                  >
                    <option value="">🕒 Versions…</option>
                    <Show when={row.type === "file"}>
                      <option value="__file_versions__">Lister les versions de ce fichier</option>
                    </Show>
                    <For each={snapshotsQuery.data?.rows ?? []}>
                      {(s) => (
                        <option value={s.mountedPath}>
                          {fmtDate(s.date)} — {s.volume || "?"}
                        </option>
                      )}
                    </For>
                  </select>
                </Show>
                <Show when={!snapshotMount()}>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    title="Supprimer"
                    onClick={() => void deleteEntry(row)}
                  >
                    🗑
                  </button>
                </Show>
              </div>
            )}
          />
        )}
      </Show>

      <Show when={!browseResult() && !browseError() && !browseLoading()}>
        <div class="empty-state">Saisir un chemin puis cliquer sur « Ouvrir ».</div>
      </Show>
    </div>
  );

  // ─── Onglet Versions précédentes (VSS) ─────────────────────────────────

  const snapshotColumns: Column<Snapshot>[] = [
    {
      id: "date",
      header: "Date",
      accessorKey: "date",
      cell: (info) => fmtDate(info.getValue() as string | null),
    },
    { id: "volume", header: "Volume", accessorKey: "volume", cell: (info) => (info.getValue() as string) || "—" },
    { id: "id", header: "ID", accessorKey: "id", cell: (info) => String(info.getValue() || "").slice(0, 40) },
  ];

  const versionsColumns: Column<FileVersionEntry>[] = [
    {
      id: "snapshotAt",
      header: "Snapshot",
      accessorKey: "snapshotAt",
      cell: (info) => fmtDate(info.getValue() as string | null),
    },
    {
      id: "lastWrite",
      header: "Modifié",
      accessorKey: "lastWrite",
      cell: (info) => fmtDate(info.getValue() as string | null),
    },
    {
      id: "sizeBytes",
      header: "Taille",
      accessorKey: "sizeBytes",
      cell: (info) => fmtBytes(info.getValue() as number),
    },
  ];

  const searchColumns: Column<SearchResultRow>[] = [
    { id: "name", header: "Nom", accessorKey: "name" },
    {
      id: "snapshotAt",
      header: "Snapshot",
      accessorKey: "snapshotAt",
      cell: (info) => fmtDate(info.getValue() as string | null),
    },
    {
      id: "fullPath",
      header: "Chemin dans snapshot",
      accessorKey: "fullPath",
      cell: (info) => {
        const v = String(info.getValue() || "");
        return v.length > 80 ? "…" + v.slice(-80) : v;
      },
    },
    {
      id: "sizeBytes",
      header: "Taille",
      accessorKey: "sizeBytes",
      cell: (info) => fmtBytes(info.getValue() as number),
    },
  ];

  const HistoryTab = () => (
    <div class="panel-data" style={{ display: "flex", "flex-direction": "column", gap: "16px" }}>
      <Show when={capsQuery.data}>
        {(caps) => (
          <p class="card-hint" style={{ margin: "0" }}>
            {caps().backend === "vss"
              ? "Source : Volume Shadow Copies (Windows). Privilèges admin requis sur l'agent."
              : "Source : Time Machine (macOS). Sauvegardes locales et externes incluses."}
          </p>
        )}
      </Show>

      {/* Snapshots disponibles */}
      <div>
        <h4 style={{ margin: "0 0 6px" }}>Snapshots disponibles</h4>
        <Show
          when={!snapshotsQuery.isFetching}
          fallback={
            <div class="versions-skeleton">
              <Skeleton variant="table" rows={5} />
            </div>
          }
        >
          <Show when={snapshotsQuery.error}>
            {(err) => (
              <div class="empty-state empty-state--error">
                Erreur : {err() instanceof Error ? (err() as Error).message : String(err())}
              </div>
            )}
          </Show>
          <DataTable
            rows={snapshotsQuery.data?.rows ?? []}
            columns={snapshotColumns}
            emptyMessage="Aucun snapshot disponible."
          />
        </Show>
      </div>

      {/* Versions d'un fichier */}
      <div>
        <h4 style={{ margin: "0 0 6px" }}>Versions d'un fichier précis</h4>
        <div class="filter-row">
          <input
            class="input"
            type="text"
            placeholder="Chemin complet du fichier (ex: C:\\Users\\Alice\\Documents\\budget.xlsx)"
            value={filePath()}
            onInput={(e) => setFilePath(e.currentTarget.value)}
            style={{ flex: "1", "min-width": "280px" }}
          />
          <button type="button" class="btn btn-primary" onClick={loadFileVersions} disabled={fileLoading()}>
            {fileLoading() ? "Recherche…" : "Chercher les versions"}
          </button>
        </div>
        <Show when={fileLoading()}>
          <div class="versions-skeleton">
            <Skeleton variant="table" rows={4} />
          </div>
        </Show>
        <Show when={!fileLoading() && fileResult()}>
          {(r) => (
            <>
              <p class="card-hint" style={{ margin: "8px 0" }}>
                <strong>Fichier actuel :</strong>{" "}
                {r().currentExists
                  ? `existe (${fmtBytes(r().currentSize)}, modifié ${fmtDate(r().currentLastWrite)})`
                  : "absent du disque (peut-être effacé)"}
              </p>
              <DataTable
                rows={r().versions}
                columns={versionsColumns}
                emptyMessage="Aucune version trouvée dans les snapshots."
                rowActions={(row) => (
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      title="Informations détaillées"
                      onClick={() => void openInfoModal(row.shadowPath)}
                    >
                      ℹ
                    </button>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={() => restore(row.shadowPath, r().filePath)}
                    >
                      Restaurer
                    </button>
                  </div>
                )}
              />
            </>
          )}
        </Show>
      </div>

      {/* Recherche */}
      <div>
        <h4 style={{ margin: "0 0 6px" }}>Recherche dans les snapshots</h4>
        <p class="card-hint" style={{ margin: "0 0 8px" }}>
          Recherche par fragment de nom dans tous les snapshots. Utile pour retrouver un fichier dont vous avez oublié
          le chemin exact, ou un fichier effacé.
        </p>
        <div class="filter-row">
          <input
            class="input"
            type="text"
            placeholder="Motif (ex: budget, *.docx, photo2024)"
            value={searchPattern()}
            onInput={(e) => setSearchPattern(e.currentTarget.value)}
            style={{ flex: "1", "min-width": "200px" }}
          />
          <input
            class="input"
            type="text"
            placeholder="Filtrer par dossier (optionnel)"
            value={searchScope()}
            onInput={(e) => setSearchScope(e.currentTarget.value)}
            style={{ flex: "1", "min-width": "200px" }}
          />
          <button type="button" class="btn btn-primary" onClick={runSearch} disabled={searchLoading()}>
            {searchLoading() ? "Recherche…" : "Rechercher"}
          </button>
        </div>
        <Show when={searchResult()}>
          {(r) => (
            <>
              <Show when={r().truncated}>
                <p class="card-hint" style={{ color: "var(--warn, #f5a623)", margin: "8px 0" }}>
                  Résultats tronqués à 200 entrées. Affinez le motif ou le filtre dossier.
                </p>
              </Show>
              <DataTable
                rows={r().rows}
                columns={searchColumns}
                emptyMessage="Aucun résultat trouvé."
                rowActions={(row) => (
                  <div style={{ display: "flex", gap: "6px" }}>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      title="Informations détaillées"
                      onClick={() => void openInfoModal(row.fullPath)}
                    >
                      ℹ
                    </button>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={() => {
                        setFilePath(
                          row.fullPath.replace(/\\\\\?\\GLOBALROOT\\Device\\HarddiskVolumeShadowCopy\d+\\/i, "C:\\"),
                        );
                        setStatus({ text: "Chemin pré-rempli dans la zone « Versions d'un fichier ».", level: "info" });
                      }}
                    >
                      Voir versions
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm" onClick={() => restore(row.fullPath, row.name)}>
                      Restaurer
                    </button>
                  </div>
                )}
              />
            </>
          )}
        </Show>
      </div>
    </div>
  );

  // ─── Onglet Téléverser ─────────────────────────────────────────────────

  const UploadTab = () => (
    <div class="panel-data" style={{ display: "flex", "flex-direction": "column", gap: "12px" }}>
      <p class="card-hint" style={{ margin: "0" }}>
        Téléverser un fichier local vers le poste distant. Limite : {fmtBytes(UPLOAD_MAX_BYTES)} par fichier.
      </p>

      <div class="filter-row">
        <input
          class="input"
          type="file"
          onChange={(e) => {
            const f = e.currentTarget.files?.[0] ?? null;
            setUploadFile(f);
          }}
          style={{ flex: "1", "min-width": "280px" }}
        />
      </div>

      <Show when={uploadFile()}>
        {(f) => (
          <p class="card-hint" style={{ margin: "0" }}>
            <strong>Sélection :</strong> {f().name} ({fmtBytes(f().size)})
            <Show when={f().size > UPLOAD_MAX_BYTES}>
              <span style={{ color: "var(--danger, #e25f5f)", "margin-left": "8px" }}>
                Trop volumineux — limite {fmtBytes(UPLOAD_MAX_BYTES)}.
              </span>
            </Show>
          </p>
        )}
      </Show>

      <div class="filter-row">
        <input
          class="input"
          type="text"
          placeholder="Dossier cible sur le poste distant (ex: C:\\Users\\Alice\\Downloads)"
          value={uploadTargetDir()}
          onInput={(e) => setUploadTargetDir(e.currentTarget.value)}
          style={{ flex: "1", "min-width": "280px" }}
        />
        <button
          type="button"
          class="btn btn-primary"
          onClick={() => void doUpload()}
          disabled={uploading() || !uploadFile() || (uploadFile()?.size ?? 0) > UPLOAD_MAX_BYTES}
        >
          {uploading() ? "Envoi…" : "Envoyer"}
        </button>
      </div>

      <Show when={uploadFile() && uploadTargetDir()}>
        <p class="card-hint" style={{ margin: "0", opacity: 0.7 }}>
          Sera écrit sous : <code>{pathJoin(uploadTargetDir(), uploadFile()!.name)}</code>
        </p>
      </Show>
    </div>
  );

  // ─── Sub-tabs ──────────────────────────────────────────────────────────

  const [activeSub, setActiveSub] = createSignal("browse");

  const subtabs: SubTab[] = [
    { id: "browse", label: "📁 Parcourir", render: () => <BrowseTab /> },
    { id: "history", label: "🕒 Versions précédentes", render: () => <HistoryTab /> },
    { id: "upload", label: "📤 Téléverser", render: () => <UploadTab /> },
  ];

  // Pilotage manuel des subtabs (pour permettre setActiveSub depuis le tableau Parcourir).
  return (
    <>
      <section class="panel-section panel-section--full">
        <div class="panel-section-header">
          <span class="eyebrow">Fichiers</span>
          <Show when={status()}>
            {(s) => (
              <span class="ad-status" data-level={s().level || "info"}>
                {s().text}
              </span>
            )}
          </Show>
        </div>

        <div class="panel-toolbar" role="tablist">
          <For each={subtabs}>
            {(st) => (
              <button
                type="button"
                class="panel-subtab"
                aria-selected={activeSub() === st.id}
                role="tab"
                onClick={() => setActiveSub(st.id)}
              >
                {st.label}
              </button>
            )}
          </For>
        </div>

        <div class="panel-data-wrap">
          <For each={subtabs}>{(st) => <Show when={activeSub() === st.id}>{st.render()}</Show>}</For>
        </div>
      </section>

      {/* ─── Modale Info ───────────────────────────────────────────────── */}
      <Show when={infoOpen()}>
        <div
          class="panel-modal-overlay"
          style={{
            position: "fixed",
            inset: "0",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            "align-items": "center",
            "justify-content": "center",
            "z-index": "9999",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeInfo();
          }}
        >
          <div
            class="panel-modal"
            style={{
              background: "#1f2128",
              color: "#e7e7e7",
              "max-width": "92vw",
              "max-height": "85vh",
              width: "780px",
              "border-radius": "8px",
              display: "flex",
              "flex-direction": "column",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "10px 14px",
                "border-bottom": "1px solid #333",
                display: "flex",
                "align-items": "center",
                "justify-content": "space-between",
                gap: "12px",
              }}
            >
              <div style={{ display: "flex", "align-items": "center", gap: "8px", "min-width": "0", flex: "1" }}>
                <Show when={infoData()} fallback={<strong style={{ "font-size": "13px" }}>Informations</strong>}>
                  {(d) => (
                    <span
                      style={{
                        padding: "2px 8px",
                        "border-radius": "999px",
                        "font-size": "11px",
                        background: d().isDirectory ? "#2f4a6e" : "#3b3552",
                        color: "#e7e7e7",
                        "white-space": "nowrap",
                      }}
                    >
                      {d().isDirectory ? "📁 dossier" : "📄 fichier"}
                    </span>
                  )}
                </Show>
                <code
                  title={infoData()?.path || infoTargetPath()}
                  style={{
                    "font-size": "12px",
                    "white-space": "nowrap",
                    overflow: "hidden",
                    "text-overflow": "ellipsis",
                    "min-width": "0",
                    flex: "1",
                  }}
                >
                  {infoData()?.path || infoTargetPath()}
                </code>
              </div>
              <button type="button" class="btn btn-ghost btn-sm" onClick={closeInfo}>
                ✕
              </button>
            </div>

            {/* Body */}
            <div
              style={{
                padding: "14px",
                overflow: "auto",
                "max-height": "70vh",
                display: "flex",
                "flex-direction": "column",
                gap: "16px",
              }}
            >
              <Show when={infoLoading()}>
                <div class="empty-state">Chargement…</div>
              </Show>
              <Show when={infoError()}>
                {(err) => <div class="empty-state empty-state--error">Erreur : {err()}</div>}
              </Show>
              <Show when={!infoLoading() && !infoError() && infoData()}>
                {(d) => (
                  <>
                    {/* Général */}
                    <section>
                      <h4 style={{ margin: "0 0 8px", "font-size": "13px", opacity: 0.85 }}>Général</h4>
                      <div
                        style={{
                          display: "grid",
                          "grid-template-columns": "140px 1fr",
                          gap: "4px 12px",
                          "font-size": "12px",
                        }}
                      >
                        <span style={{ opacity: 0.7 }}>Taille</span>
                        <span>{d().isDirectory ? "—" : fmtBytes(d().sizeBytes ?? 0)}</span>
                        <span style={{ opacity: 0.7 }}>Créé</span>
                        <span>{fmtDate(d().createdAt)}</span>
                        <span style={{ opacity: 0.7 }}>Modifié</span>
                        <span>{fmtDate(d().modifiedAt)}</span>
                        <span style={{ opacity: 0.7 }}>Accédé</span>
                        <span>{fmtDate(d().accessedAt)}</span>
                        <span style={{ opacity: 0.7 }}>Attributs</span>
                        <span style={{ display: "flex", "flex-wrap": "wrap", gap: "4px" }}>
                          <Show when={d().isReadOnly}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#5a4530",
                                "font-size": "11px",
                              }}
                            >
                              ReadOnly
                            </span>
                          </Show>
                          <Show when={d().isHidden}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#3a3a3a",
                                "font-size": "11px",
                              }}
                            >
                              Hidden
                            </span>
                          </Show>
                          <Show when={d().isSystem}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#5a3030",
                                "font-size": "11px",
                              }}
                            >
                              System
                            </span>
                          </Show>
                          <Show when={d().isArchive}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#2f4a6e",
                                "font-size": "11px",
                              }}
                            >
                              Archive
                            </span>
                          </Show>
                          <Show when={d().isCompressed}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#2f6e4a",
                                "font-size": "11px",
                              }}
                            >
                              Compressed
                            </span>
                          </Show>
                          <Show when={d().isEncrypted}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#6e2f5a",
                                "font-size": "11px",
                              }}
                            >
                              Encrypted
                            </span>
                          </Show>
                          <Show when={d().isReparsePoint}>
                            <span
                              style={{
                                padding: "1px 6px",
                                "border-radius": "4px",
                                background: "#6e5a2f",
                                "font-size": "11px",
                              }}
                            >
                              ReparsePoint
                            </span>
                          </Show>
                          <Show
                            when={
                              !d().isReadOnly &&
                              !d().isHidden &&
                              !d().isSystem &&
                              !d().isArchive &&
                              !d().isCompressed &&
                              !d().isEncrypted &&
                              !d().isReparsePoint
                            }
                          >
                            <span style={{ opacity: 0.6, "font-size": "11px" }}>{d().attributes || "—"}</span>
                          </Show>
                        </span>
                      </div>
                    </section>

                    {/* Propriétaire */}
                    <section>
                      <h4 style={{ margin: "0 0 8px", "font-size": "13px", opacity: 0.85 }}>Propriétaire</h4>
                      <div style={{ "font-size": "12px" }}>
                        <div>{d().owner || <span style={{ opacity: 0.6 }}>(inconnu)</span>}</div>
                        <Show when={d().ownerSid}>
                          <code style={{ "font-size": "11px", opacity: 0.7 }}>{d().ownerSid}</code>
                        </Show>
                      </div>
                    </section>

                    {/* Métadonnées exécutable (FileVersionInfo) */}
                    <Show when={d().metadata}>
                      {(md) => {
                        const m = md();
                        const hasAny =
                          m.fileVersion ||
                          m.productVersion ||
                          m.productName ||
                          m.companyName ||
                          m.fileDescription ||
                          m.originalFilename ||
                          m.internalName ||
                          m.legalCopyright;
                        return (
                          <Show when={hasAny}>
                            <section>
                              <h4 style={{ margin: "0 0 8px", "font-size": "13px", opacity: 0.85 }}>
                                Métadonnées (FileVersionInfo)
                              </h4>
                              <div
                                style={{
                                  display: "grid",
                                  "grid-template-columns": "160px 1fr",
                                  gap: "4px 12px",
                                  "font-size": "12px",
                                }}
                              >
                                <Show when={m.productName}>
                                  <span style={{ opacity: 0.7 }}>Produit</span>
                                  <span>{m.productName}</span>
                                </Show>
                                <Show when={m.fileDescription}>
                                  <span style={{ opacity: 0.7 }}>Description</span>
                                  <span>{m.fileDescription}</span>
                                </Show>
                                <Show when={m.companyName}>
                                  <span style={{ opacity: 0.7 }}>Éditeur</span>
                                  <span>{m.companyName}</span>
                                </Show>
                                <Show when={m.fileVersion}>
                                  <span style={{ opacity: 0.7 }}>Version fichier</span>
                                  <span>
                                    <code>{m.fileVersion}</code>
                                  </span>
                                </Show>
                                <Show when={m.productVersion && m.productVersion !== m.fileVersion}>
                                  <span style={{ opacity: 0.7 }}>Version produit</span>
                                  <span>
                                    <code>{m.productVersion}</code>
                                  </span>
                                </Show>
                                <Show when={m.originalFilename}>
                                  <span style={{ opacity: 0.7 }}>Nom d'origine</span>
                                  <span>
                                    <code>{m.originalFilename}</code>
                                  </span>
                                </Show>
                                <Show when={m.internalName && m.internalName !== m.originalFilename}>
                                  <span style={{ opacity: 0.7 }}>Nom interne</span>
                                  <span>
                                    <code>{m.internalName}</code>
                                  </span>
                                </Show>
                                <Show when={m.legalCopyright}>
                                  <span style={{ opacity: 0.7 }}>Copyright</span>
                                  <span style={{ opacity: 0.85 }}>{m.legalCopyright}</span>
                                </Show>
                                <Show when={m.language}>
                                  <span style={{ opacity: 0.7 }}>Langue</span>
                                  <span>{m.language}</span>
                                </Show>
                                <Show when={m.isDebug || m.isPatched || m.isPreRelease}>
                                  <span style={{ opacity: 0.7 }}>Drapeaux</span>
                                  <span style={{ display: "flex", "flex-wrap": "wrap", gap: "4px" }}>
                                    <Show when={m.isDebug}>
                                      <span
                                        style={{
                                          padding: "1px 6px",
                                          "border-radius": "4px",
                                          background: "#5a4530",
                                          "font-size": "11px",
                                        }}
                                      >
                                        Debug
                                      </span>
                                    </Show>
                                    <Show when={m.isPatched}>
                                      <span
                                        style={{
                                          padding: "1px 6px",
                                          "border-radius": "4px",
                                          background: "#6e2f5a",
                                          "font-size": "11px",
                                        }}
                                      >
                                        Patched
                                      </span>
                                    </Show>
                                    <Show when={m.isPreRelease}>
                                      <span
                                        style={{
                                          padding: "1px 6px",
                                          "border-radius": "4px",
                                          background: "#2f4a6e",
                                          "font-size": "11px",
                                        }}
                                      >
                                        Pre-Release
                                      </span>
                                    </Show>
                                  </span>
                                </Show>
                              </div>
                            </section>
                          </Show>
                        );
                      }}
                    </Show>

                    {/* Signature numérique (Authenticode) */}
                    <Show when={d().signature}>
                      {(sg) => {
                        const s = sg();
                        return (
                          <section>
                            <h4 style={{ margin: "0 0 8px", "font-size": "13px", opacity: 0.85 }}>
                              Signature numérique
                              <Show when={s.signed}>
                                <span
                                  style={{
                                    "margin-left": "8px",
                                    padding: "1px 8px",
                                    "border-radius": "999px",
                                    background: "#2f6e4a",
                                    color: "#e7e7e7",
                                    "font-size": "10px",
                                    "font-weight": "600",
                                  }}
                                >
                                  ✓ Signé
                                </span>
                              </Show>
                              <Show when={!s.signed}>
                                <span
                                  style={{
                                    "margin-left": "8px",
                                    padding: "1px 8px",
                                    "border-radius": "999px",
                                    background: "#5a3030",
                                    color: "#e7e7e7",
                                    "font-size": "10px",
                                    "font-weight": "600",
                                  }}
                                >
                                  ✗ Non signé
                                </span>
                              </Show>
                            </h4>
                            <Show when={s.signed}>
                              <div
                                style={{
                                  display: "grid",
                                  "grid-template-columns": "160px 1fr",
                                  gap: "4px 12px",
                                  "font-size": "12px",
                                }}
                              >
                                <Show when={s.subject}>
                                  <span style={{ opacity: 0.7 }}>Sujet</span>
                                  <span style={{ "word-break": "break-all" }}>{s.subject}</span>
                                </Show>
                                <Show when={s.issuer}>
                                  <span style={{ opacity: 0.7 }}>Émetteur</span>
                                  <span style={{ "word-break": "break-all" }}>{s.issuer}</span>
                                </Show>
                                <Show when={s.notBefore}>
                                  <span style={{ opacity: 0.7 }}>Valide depuis</span>
                                  <span>{fmtDate(s.notBefore)}</span>
                                </Show>
                                <Show when={s.notAfter}>
                                  <span style={{ opacity: 0.7 }}>Valide jusqu'à</span>
                                  <span>{fmtDate(s.notAfter)}</span>
                                </Show>
                                <Show when={s.signatureAlgorithm}>
                                  <span style={{ opacity: 0.7 }}>Algorithme</span>
                                  <span>{s.signatureAlgorithm}</span>
                                </Show>
                                <Show when={s.thumbprint}>
                                  <span style={{ opacity: 0.7 }}>Empreinte</span>
                                  <code style={{ "font-size": "11px", "word-break": "break-all" }}>{s.thumbprint}</code>
                                </Show>
                                <Show when={s.serialNumber}>
                                  <span style={{ opacity: 0.7 }}>N° série</span>
                                  <code style={{ "font-size": "11px", "word-break": "break-all" }}>
                                    {s.serialNumber}
                                  </code>
                                </Show>
                              </div>
                            </Show>
                          </section>
                        );
                      }}
                    </Show>

                    {/* ACL */}
                    <section>
                      <h4 style={{ margin: "0 0 8px", "font-size": "13px", opacity: 0.85 }}>ACL</h4>
                      <Show
                        when={d().acl && d().acl.length > 0}
                        fallback={
                          <div class="empty-state" style={{ "font-size": "12px" }}>
                            Pas de règle ACL accessible (droits insuffisants ?)
                          </div>
                        }
                      >
                        <table
                          style={{
                            width: "100%",
                            "border-collapse": "collapse",
                            "font-size": "12px",
                          }}
                        >
                          <thead>
                            <tr style={{ "text-align": "left", "border-bottom": "1px solid #333" }}>
                              <th style={{ padding: "4px 6px" }}>Identité</th>
                              <th style={{ padding: "4px 6px" }}>Type</th>
                              <th style={{ padding: "4px 6px" }}>Droits</th>
                              <th style={{ padding: "4px 6px" }}>Hérité</th>
                            </tr>
                          </thead>
                          <tbody>
                            <For each={d().acl}>
                              {(a) => (
                                <tr style={{ "border-bottom": "1px solid #2a2a2a" }}>
                                  <td style={{ padding: "4px 6px", "word-break": "break-all" }}>{a.identity}</td>
                                  <td
                                    style={{
                                      padding: "4px 6px",
                                      color: a.type === "Deny" ? "#e25f5f" : "#4ec07a",
                                      "font-weight": "600",
                                    }}
                                  >
                                    {a.type}
                                  </td>
                                  <td style={{ padding: "4px 6px" }}>{a.rights}</td>
                                  <td style={{ padding: "4px 6px", opacity: 0.8 }}>{a.inherited ? "oui" : "non"}</td>
                                </tr>
                              )}
                            </For>
                          </tbody>
                        </table>
                      </Show>
                    </section>
                  </>
                )}
              </Show>
            </div>

            {/* Footer */}
            <div
              style={{
                padding: "10px 14px",
                "border-top": "1px solid #333",
                display: "flex",
                "justify-content": "flex-end",
              }}
            >
              <button type="button" class="btn btn-primary" onClick={closeInfo}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* ─── Aperçu rapide (Quick Look) ───────────────────────────────── */}
      <Drawer open={quickOpen()} onClose={closeQuickLook} title="Aperçu rapide" width="520px">
        <div class="versions-quicklook">
          <div class="versions-quicklook__meta">
            <code class="versions-quicklook__path" title={quickPath()}>
              {quickPath() || quickName()}
            </code>
            <Show when={quickSize() != null}>
              <span class="versions-quicklook__size">{fmtBytes(quickSize())}</span>
            </Show>
          </div>

          <Show when={quickLoading()}>
            <div class="versions-skeleton">
              <Skeleton variant="block" height="220px" />
            </div>
          </Show>

          <Show when={!quickLoading() && quickError()}>
            {(err) => <div class="empty-state empty-state--error">{err()}</div>}
          </Show>

          <Show when={!quickLoading() && !quickError() && quickKind() === "image"}>
            <div class="versions-quicklook__image-wrap">
              <Show when={quickImageUrl()} fallback={<div class="empty-state">Image vide.</div>}>
                <img class="versions-quicklook__image" src={quickImageUrl()} alt={quickName()} />
              </Show>
            </div>
          </Show>

          <Show when={!quickLoading() && !quickError() && quickKind() === "text"}>
            <pre class="versions-quicklook__text">{quickText() || "(fichier vide)"}</pre>
          </Show>
        </div>
      </Drawer>
    </>
  );
};

registerPanel("versions", VersionsPanel);

export default VersionsPanel;
